import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { DiffFlow, generateRandomHash, objectToHash, useWindowSize } from '../../core';
import {
  ResizablePanel,
  CssEditor,
  BaseNode,
  BaseNodeData,
  ConfirmModal,
  TableNode,
  TableNodeData,
  Group,
  TableNodeStyle,
  LegendNode,
  LegendNodeData,
  LegendNodeStyle,
} from '../../components';
import {
  addEdge,
  Background,
  Controls,
  Edge,
  EdgeMouseHandler,
  HandleProps,
  HandleType,
  Node,
  NodeMouseHandler,
  OnConnect,
  OnNodesChange,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ContentCopy, Delete, ExpandMore, Lock, LockOpen, Settings } from '@mui/icons-material';
import { formasStyles, SelectFormas } from '../../components/diagrama/formas';
import { Flow, Node as ContextNode, Edge as ContextEdge, useFlowContext, useHistoryContext } from '../../context/FluxogramaContext';
import { deepEqual } from '../../core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const nodeTypes: any = {
  baseNode: BaseNode,
  tableNode: TableNode,
  legendNode: LegendNode,
};

export type MyNodeType = BaseNodeData | TableNodeData | LegendNodeData;

export interface DiagramaProps {
  justView?: boolean;
}

export const Diagrama: React.FC<DiagramaProps> = ({ justView }) => {
  const { palette } = useTheme();
  const { width } = useWindowSize();
  const { undo, redo } = useHistoryContext();
  const { flow, setFlow } = useFlowContext();
  const { screenToFlowPosition } = useReactFlow();

  const isSyncing = useRef(false);
  const isStarting = useRef(true);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    node: Node<MyNodeType>;
  } | null>(null);
  const [flowKey, setFlowKey] = useState<string>();
  const [cssEditorKey, setCssEditorKey] = useState<string>();
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<Node<MyNodeType>>();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<MyNodeType>>([]);
  const [confim, setConfirm] = useState<{ title: string; message: string; onConfirm: () => void; }>();

  useEffect(() => {
    if (!justView) return;

    setNodes(nodes => nodes.map(node => {
      node.data.lock = true;
      return node;
    }));

    setTimeout(() => {
      isStarting.current = false;
    }, 100);
  }, []);

  useEffect(() => {
    if (isSyncing.current) {
      isSyncing.current = false;
      return;
    }

    if (!flow) return;

    // Selecioando o minX e minY para redefinir o 0 e evitar overflow
    const minXY = flow.nodes.reduce((ans, now) => {
      if (now.style.parentId) {
        return [-1, -1];
      }

      let minX = ans[0];
      let minY = ans[1];

      if (minX === -1) minX = now.posX;
      if (minY === -1) minY = now.posY;

      return [
        Math.min(minX, now.posX),
        Math.min(minY, now.posY),
      ];
    }, [-1, -1]);

    // Colocando as tabelas na frente para a rederização funcionar
    const nodes = [...flow.nodes.filter(no => "columns" in no.style), ...flow.nodes.filter(no => !("columns" in no.style))];

    setNodes(nodes.map((n) => {
      return {
        id: n.id,
        position: {
          x: n.posX - (n.style.parentId ? 0 : minXY[0]),
          y: n.posY - (n.style.parentId ? 0 : minXY[1]),
        },
        data: {
          markdown: n.markdown,
          lock: justView,
          style: n.style as TableNodeStyle, // Não é somente TableNodeStyle, mas pra evitar erros assim funciona
          handles: n.handles.map((h) => ({
            ...h,
            position: toRFPosition(h.position),
          })),
          onChange: (node: { data: MyNodeType; }) => {
            setNodes(nds => nds.map(no => {
              if (no.id !== n.id) return no;
              no.data = node.data;
              return no;
            }));
          },
        },
        parentId: n.style.parentId,
        type: ("tableStyle" in n.style) ? "tableNode" : (
          "legendStyle" in n.style ? "legendNode" : 'baseNode'
        ),
      };
    }));

    setEdges(flow.edges.map((e) => {
      const source = nodes.find(no => no.handles.find(hand => hand.id === e.handleSourceId));
      if (!source) return undefined;

      const target = nodes.find(no => no.handles.find(hand => hand.id === e.handleTargetId));
      if (!target) return undefined;

      return {
        id: e.id,
        source: source.id,
        sourceHandle: e.handleSourceId,
        target: target.id,
        targetHandle: e.handleTargetId,
        style: e.style || {},
        data: { markdown: e.markdown },
      };

    }).filter(no => no !== undefined) as Edge[]);
  }, [flow]);

  useEffect(() => {
    if (flow) {
      const converted = convertFlow(flow, nodes, edges);

      if (!deepEqual(flow, converted)) {
        isSyncing.current = true;
        setFlow(converted);
      }
    }
  }, [nodes, edges]);

  // Ctrl+Z e Ctrl+Y
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      let changes;
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        changes = undo();
      } else if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        changes = redo();
      }

      if (!changes) return;
      const { newNodes, newEdges } = diffToNodesEdges(changes, nodes, edges);

      setNodes(newNodes);
      setEdges(newEdges);
      // setFlowKey(`${objectToHash(newNodes)}-${objectToHash(newEdges)}`);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, nodes, edges]);


  function diffToNodesEdges(diff: DiffFlow, nodes: Node<MyNodeType>[], edges: Edge[]) {

    // Removendo os nós, handles ou edges que foram adicionados
    let newNodes = nodes.filter(no => !diff.nodes.created.find(diffNo => diffNo.id === no.id));

    // Adicionando os nós que foram removidos
    newNodes = newNodes.concat(diff.nodes.deleted.map((n) => {
      return {
        id: n.id,
        position: {
          x: n.posX,
          y: n.posY,
        },
        data: {
          markdown: n.markdown,
          style: { ...n.style } as TableNodeStyle, // Não é somente TableNodeStyle, mas pra evitar erros assim funciona
          handles: n.handles.map((h) => ({
            ...h,
            position: toRFPosition(h.position),
          })),
          onChange: (node: { data: MyNodeType; }) => {
            setNodes(nds => nds.map(no => {
              if (no.id !== n.id) return no;
              no.data = node.data;
              return no;
            }));
          },
        },
        parentId: n.style.parentId,
        type: ("tableStyle" in n.style) ? "tableNode" : (
          "legendStyle" in n.style ? "legendNode" : 'baseNode'
        ),
      };
    }));

    // Voltando as modificações dos nós
    newNodes = newNodes.map(n => {
      const modified = diff.nodes.updated.find(no => no.id === n.id);
      if (!modified) return n;

      return {
        id: n.id,
        position: {
          x: modified.posX,
          y: modified.posY,
        },
        data: {
          markdown: modified.markdown,
          style: modified.style as TableNodeStyle, // Não é somente TableNodeStyle, mas pra evitar erros assim funciona
          handles: modified.handles.map((h) => ({
            ...h,
            position: toRFPosition(h.position),
          })),
          onChange: (node: { data: MyNodeType; }) => {
            setNodes(nds => nds.map(no => {
              if (no.id !== n.id) return no;
              no.data = node.data;
              return no;
            }));
          },
        },
        parentId: modified.style.parentId,
        type: ("tableStyle" in modified.style) ? "tableNode" : (
          "legendStyle" in modified.style ? "legendNode" : 'baseNode'
        ),
      };
    });

    // Adicionando os Edges que foram removidos
    const newEdges = edges.filter(ed => !diff.edges.created.find(diffEd => diffEd.id === ed.id));
    // newEdges = newEdges.concat(diff.edges.deleted.map((e) => {
    //   const source = newNodes.find(no => no.data.handles.find(hand => hand.id === e.handleSourceId));
    //   if (!source) return undefined;

    //   const target = newNodes.find(no => no.data.handles.find(hand => hand.id === e.handleTargetId));
    //   if (!target) return undefined;

    //   return {
    //     id: e.id,
    //     source: source.id,
    //     sourceHandle: e.handleSourceId,
    //     target: target.id,
    //     targetHandle: e.handleTargetId,
    //     style: e.style || {},
    //     data: { markdown: e.markdown },
    //   };
    // }).filter(ed => ed !== undefined) as Edge[]);



    // Atualizando os Handles que foram modificados, sim atualizo os nós!
    //Remover
    // newNodes = newNodes.map(node => {
    //   const handlesRemove = diff.handles.created.filter(hand => hand.nodeId === node.id).map((h) => ({
    //     ...h, position: toRFPosition(h.position)
    //   }));
    //   node.data.handles = node.data.handles.filter(hand => !handlesRemove.find(h => hand.id === h.id));
    //   return node;
    // });
    // //Adicionar
    // newNodes = newNodes.map(node => {
    //   const handlesAdd = diff.handles.deleted.filter(hand => hand.nodeId === node.id).map((h) => ({
    //     ...h, position: toRFPosition(h.position)
    //   }));
    //   node.data.handles = node.data.handles.concat(handlesAdd);
    //   return node;
    // });
    // // Modificar, sim atualizo os nós!
    // newNodes = newNodes.map(node => {
    //   const handleChange = diff.handles.updated.filter(hand => hand.nodeId === node.id).map((h) => ({
    //     ...h, position: toRFPosition(h.position)
    //   }));
    //   node.data.handles = node.data.handles.map(hand => {
    //     const ansHand = handleChange.find(h => h.id === hand.id);
    //     if (!ansHand) return hand;

    //     return {
    //       ...hand, position: toRFPosition(hand.position)
    //     };
    //   });
    //   return node;
    // });

    // Voltando as modificações dos Edges
    // newEdges = newEdges.map(e => {
    //   const modified = diff.edges.updated.find(ed => ed.id === e.id);
    //   if (!modified) return e;

    //   return {
    //     id: e.id,
    //     source: e.source,
    //     target: e.target,

    //     style: modified.style,
    //     sourceHandle: modified.handleSourceId,
    //     targetHandle: modified.handleTargetId,
    //     data: { markdown: modified.markdown },
    //   };
    // });

    // console.log(newNodes);
    
    return { newNodes, newEdges };
  }

  const onChangeNode = (newNode: Node<BaseNodeData>) => {
    setNodes(nds => nds.map(nd => {
      if (nd.id === newNode.id) {
        nd.data = newNode.data;
        nd.height = newNode.height;
        nd.width = newNode.width;
      }
      return nd;
    }));
  };

  const onNodeClick: NodeMouseHandler<Node<MyNodeType>> = (_, node) => {
    if (node.data.lock) return;

    setSelectedNode(node);
    setCssEditorKey(generateRandomHash(8));
  };

  const onEdgeDoubleClick: EdgeMouseHandler = (_, edge) => {
    const edges_ = edges.filter(val => val.id !== edge.id);
    setEdges(edges_);
  };

  const onPaneClick = () => {
    setSelectedNode(undefined);
  };

  const removeTableNode = (node: Node<BaseNodeData>) => {
    const table = nodes.find(n => n.id === node.parentId);
    setNodes(nds => nds.map(n => {
      if (n.id === node.id) {
        const position = {
          x: node.position.x + (table?.position.x ?? 0),
          y: node.position.y + (table?.position.y ?? 0),
        };

        // Gambiarra mesmo, a posição atualiza junto com o parentId
        setTimeout(() => {
          setNodes(nds => nds.map(n => {
            if (n.id === node.id) {
              return {
                ...n,
                position: position,
              };
            }
            return n;
          }));

        }, 0);

        return {
          ...n,
          parentId: undefined,
          position: position,
        };
      }

      return n;
    }));
  };

  const setNodeToTable = (node: Node<BaseNodeData>, table: Node<TableNodeData>) => {
    setNodes(nds => nds.map(n => {
      if (n.id === node.id) {
        const position = {
          x: n.position.x - table.position.x,
          y: n.position.y - table.position.y,
        };

        // Gambiarra mesmo, a posição atualiza junto com o parentId
        setTimeout(() => {
          setNodes(nds => nds.map(n => {
            if (n.id === node.id) {
              return {
                ...n,
                position: position,
              };
            }
            return n;
          }));

        }, 0);

        return {
          ...n,
          position: position,
          parentId: table.id,
        };
      }
      return n;
    }));
  };

  const handleNodeChange: OnNodesChange<Node<MyNodeType>> = (changes) => {
    // Removendo os nós bloqueados
    const blockedIds = nodes.filter(no => no.data.lock ?? false).map(no => no.id);
    if (blockedIds.length > 0) changes = changes.filter(no => !("id" in no) || !blockedIds.includes(no.id));

    // Verificações das modificações
    const tables = nodes.filter(no => no.type === "tableNode");
    changes = changes.map(change => {
      if (change.type === "position" && change.dragging === false) {
        const node = nodes.find(no => "id" in change && no.id === change.id);
        if (node && node.type !== "tableNode") {
          node.position = change.position ?? node.position;
          const table = tables.find(tab => isNodeInside(node, tab));

          if (table && node.parentId !== table.id) {
            setNodeToTable(node as Node<BaseNodeData>, table as Node<TableNodeData>);
          } else if (!table && node.parentId) {
            removeTableNode(node as Node<BaseNodeData>);
          }
        }
      } else if (change.type === 'dimensions') {
        const node = nodes.find(no => "id" in change && no.id === change.id);
        if (node) {
          if (node.type === "baseNode") {
            const style: CSSProperties = (node.data.style as CSSProperties);
            (node?.data as BaseNodeData).style.height = style.height ?? node.measured?.height;
            (node?.data as BaseNodeData).style.width = style.width ?? node.measured?.width;
          } else if (node.type === "tableNode") {
            const style: TableNodeStyle = (node.data.style as TableNodeStyle);
            (node?.data as TableNodeData).style.tableStyle.height = style.tableStyle.height ?? node.measured?.height;
            (node?.data as TableNodeData).style.tableStyle.width = style.tableStyle.width ?? node.measured?.width;
          }
        }
      }

      return change;
    });

    onNodesChange(changes);
  };

  const handleEditNodeCss = (style: React.CSSProperties) => {
    if (!selectedNode || selectedNode.data.lock) return;

    const nodes_ = nodes.map(val => {
      if (val.id == selectedNode.id) {
        if (val.type === "tableNode") {
          (val.data.style as TableNodeStyle).tableStyle = style;
        } else if (val.type === "baseNode") {
          val.data.style = style;
        } else if (val.type === "legendNode") {
          (val.data.style as LegendNodeStyle).legendStyle = style;
        }
      }
      return val;
    });
    setNodes(nodes_);
  };

  const handleAddHandle = () => {
    if (!selectedNode) return;

    const nodes_ = nodes.map(node => {
      if (node.id !== selectedNode.id) return node;

      node.data.handles.push({
        id: generateRandomHash(8),
        position: Position.Top,
        type: "source",
      });

      return node;

    });

    setNodes(nodes_);
  };

  const handleRemoveHandle = (handle: HandleProps) => {
    if (!selectedNode || selectedNode.data.lock) return;

    const nodes_ = nodes.map(node => {
      if (node.id !== selectedNode.id) return node;
      node.data.handles = selectedNode.data.handles.filter(val => val.id !== handle.id);
      return node;
    });

    setNodes(nodes_);
    setCssEditorKey(generateRandomHash(8));
  };

  const handleDuplicateNode = () => {
    if (!contextMenu?.node) return;

    contextMenu.node.data.lock = undefined;

    const node = {
      ...contextMenu.node,
      position: {
        x: contextMenu.node.position.x + 50,
        y: contextMenu.node.position.y + 50,
      },
      data: { ...contextMenu.node.data },
      id: generateRandomHash(8),
    };

    setNodes([...nodes, node]);
    setContextMenu(null);
  };

  const handleEditNode = (node: Node<MyNodeType>) => {
    if (node.data.lock) return;

    if (contextMenu) setContextMenu(null);
    setSelectedNode(node);
    setCssEditorKey(generateRandomHash(8));
  };

  const handleBlockNode = (node: Node<MyNodeType>) => {
    if (contextMenu) setContextMenu(null);

    const nodes_ = nodes.map(no => {
      if (no.id === node.id) {
        no.data.lock = !(no.data.lock ?? false);
      }
      return no;
    });

    setNodes(nodes_);
  };

  const handleRemoveNode = (node: Node<MyNodeType>) => {
    if (node.data.lock) return;

    if (contextMenu) setContextMenu(null);

    const edges_ = edges.filter(ed => !(ed.target === node.id || ed.source === node.id));
    setEdges(edges_);

    const nodes_ = nodes.filter(no => no.id !== node.id);
    setNodes(nodes_);
    setContextMenu(null);
  };

  const handleNodeContextMenu = (event: React.MouseEvent, node: Node<MyNodeType>) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      node: node,
    });
  };

  const handleEditHandle = (handle: HandleProps) => {
    if (!selectedNode || selectedNode.data.lock) return;

    const nodes_ = nodes.map(node => {
      if (node.id !== selectedNode.id) return node;
      node.data.handles = selectedNode.data.handles.map(val => {
        if (handle.id === val.id) {

          // Resetando os Edges que se conectão ao handle
          if (handle.type !== val.type) {
            const newEdges = edges.filter(val => val.sourceHandle !== handle.id && val.targetHandle !== handle.id);
            setEdges(newEdges);
          }

          return {
            ...val,
            position: handle.position,
            type: handle.type,
            style: {},
          };
        }
        return val;
      });
      return node;
    });

    setNodes(nodes_);
    setFlowKey(objectToHash(nodes_));
  };

  const handleDropNewNode = (ev: React.DragEvent, type: string) => {
    const position = screenToFlowPosition({
      x: ev.clientX,
      y: ev.clientY,
    });

    const style = formasStyles[type];

    const newNode: Node<MyNodeType> = {
      id: generateRandomHash(8),
      type: 'baseNode',
      position: position,
      data: {
        onChange: onChangeNode,
        markdown: "**Texto**",
        handles: [
          {
            id: generateRandomHash(8),
            position: Position.Left,
            type: "target",
          },
          {
            id: generateRandomHash(8),
            position: Position.Right,
            type: "source",
          },
        ],
        style: { ...style, height: 200, width: 200 },
      },
    };

    const table = nodes.filter(no => no.type === "tableNode").find(tab => isNodeInside(newNode, tab));
    if (table) {
      newNode.parentId = table.id;
      newNode.position.x += table.position.x;
      newNode.position.y += table.position.y;
    }

    setNodes([...nodes, newNode]);
  };

  const handleDropNewTable = (ev: React.DragEvent, table: { title?: string; rows: number; cols: number; }) => {

    const position = screenToFlowPosition({
      x: ev.clientX,
      y: ev.clientY,
    });

    const tableId = generateRandomHash(8);

    const newNode: Node<TableNodeData> = {
      id: tableId,
      type: 'tableNode',
      position: position,
      data: {
        onChange: (table) => {
          setNodes(nds => nds.map(no => {
            if (no.id !== tableId) return no;
            no.data = table.data;
            return no;
          }));
        },
        markdown: table.title,
        handles: [],
        style: {
          tableStyle: {
            height: 500,
            width: 800,
          },
          columns: Array.from({ length: table.cols }, () => {
            return {
              width: 100,
              rows: Array.from({ length: table.rows }, (idx) => {
                return {
                  height: idx === 0 ? 10 : 100,
                  style: {
                    backgroundColor: "#FFF0",
                  }
                };
              })
            };
          })
        },
      },
    };

    setNodes([...nodes, newNode]);
  };

  const handleDropNewLegend = (ev: React.DragEvent) => {
    const position = screenToFlowPosition({
      x: ev.clientX,
      y: ev.clientY,
    });

    const legendId = generateRandomHash(8);

    const newNode: Node<LegendNodeData> = {
      id: legendId,
      type: 'legendNode',
      position: position,
      data: {
        onChange: (legend) => {
          setNodes(nds => nds.map(no => {
            if (no.id !== legendId) return no;
            no.data = legend.data;
            return no;
          }));
        },
        markdown: "**LEGENDA**",
        handles: [],
        style: {},
      },
    };

    setNodes([...nodes, newNode]);
  };

  const onConnect: OnConnect = useCallback(
    (connection) => {
      if (connection.source == connection.target) return;
      setEdges((edges) => addEdge(connection, edges).map(edge => {
        return {
          ...edge,
          zIndex: 20,
          style: {
            ...edge.style,
            stroke: '#000',
            strokeWidth: 2,
            opacity: 0.9,
          }
        };
      }));
    },
    [setEdges]
  );


  return (
    <>
      {confim && <ConfirmModal
        open={Boolean(confim)}
        onCancel={() => setConfirm(undefined)}
        {...confim}
      />}

      {!justView && contextMenu &&
        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >

          <MenuItem onClick={() => handleDuplicateNode()}>
            <Item title={"Criar uma Cópia"} icon={<ContentCopy />} />
          </MenuItem>

          <MenuItem onClick={() => handleBlockNode(contextMenu.node)}>
            <Item title={"Travar"} icon={(contextMenu.node.data.lock ?? false) ? <Lock /> : <LockOpen />} />
          </MenuItem>

          {!(contextMenu.node.data.lock ?? false) &&
            < MenuItem onClick={() => handleEditNode(contextMenu.node)}>
              <Item title={"Propriedades"} icon={<Settings />} />
            </MenuItem>
          }

          {!(contextMenu.node.data.lock ?? false) &&
            <MenuItem onClick={() => setConfirm({
              title: "Deseja remover este nó?",
              message: "Esta ação é irreversível!",
              onConfirm: () => {
                setConfirm(undefined);
                handleRemoveNode(contextMenu.node);
              },
            })}>
              <Item title={"Excluir"} icon={<Delete />} />
            </MenuItem>
          }

        </Menu >
      }

      <div style={{ display: 'flex', flexDirection: "row", height: '100%' }}>

        {/* Items padrão */}
        {!justView && (
          <ResizablePanel
            isExtreme={true}
            initialWidth={width * 0.20}
            borderColor={palette.divider}
          >
            <Box sx={{
              width: "100%",
              height: "100%",
              padding: "1em",
              display: "flex",
              overflow: "auto",
              flexDirection: 'column',
              justifyContent: "center",
              alignItems: "center",
            }}>
              <SelectFormas
                onDrop={handleDropNewNode}
                onDropTable={handleDropNewTable}
                onDropLegend={handleDropNewLegend}
              />
            </Box>
          </ResizablePanel>
        )}
        
        {/* Flow */}
        <Box sx={{ flex: 1 }}>
          <ReactFlow<Node<MyNodeType>>
            key={flowKey}
            fitView={isStarting.current}
            nodes={nodes}
            edges={edges}
            maxZoom={2000}
            minZoom={0}
            nodeTypes={nodeTypes}
            // edgeTypes={edgeTypes}
            onNodesChange={justView ? undefined : handleNodeChange}
            onEdgesChange={justView ? undefined : onEdgesChange}
            onConnect={onConnect}
            onPaneClick={onPaneClick}
            onNodeClick={onNodeClick}

            // onNodeDoubleClick={onNodeDoubleClick}
            onEdgeDoubleClick={onEdgeDoubleClick}

            onNodeContextMenu={handleNodeContextMenu}
          >
            <Background />
            <Controls showInteractive={!justView} />
          </ReactFlow>
        </Box>

        {/* Editor de CSS */}
        {!justView && selectedNode &&
          <ResizablePanel
            initialWidth={width * 0.20}
            borderColor={palette.divider}
            sx={{ paddingLeft: "1em", paddingRight: "1em", paddingTop: "0.5em", overflow: "auto" }}
          >
            <CssEditor
              key={cssEditorKey}
              type='div'
              initialStyle={
                "tableStyle" in selectedNode.data.style ? selectedNode.data.style.tableStyle : (
                  "legendStyle" in selectedNode.data.style ? selectedNode.data.style.legendStyle : (
                    selectedNode.data.style as CSSProperties
                  )
                )
              }
              preGroups={generatePreGroups(
                selectedNode,
                handleAddHandle,
                handleRemoveHandle,
                handleEditHandle,
              )}
              onEdit={handleEditNodeCss}
            />
          </ResizablePanel>
        }
      </div>
    </>
  );
};

interface HandleItem extends HandleProps {
  name: string;
  onExpand?: (handle: HandleProps, expanded: boolean) => void;
  onEditHandle?: (handle: HandleProps) => void;
  onRemoveHandle?: (handle: HandleProps) => void;
}

const HandleItem: React.FC<HandleItem> = (props) => {
  const [handleProps, setHandleProps] = useState(props);
  const [confim, setConfirm] = useState<{ title: string; message: string; onConfirm: () => void; }>();

  const handlePosition = (position: string) => {
    const pos = position === 'bottom' ? Position.Bottom : (
      position === 'top' ? Position.Top : (
        position === 'left' ? Position.Left : Position.Right
      )
    );
    const newHandle = {
      ...handleProps,
      position: pos,
    };
    setHandleProps(newHandle);
    props.onEditHandle?.(newHandle);
  };

  const handleType = (type: HandleType) => {
    setConfirm(undefined);

    const newHandle = {
      ...handleProps,
      type,
    };
    setHandleProps(newHandle);
    props.onEditHandle?.(newHandle);
  };

  const handleRemove = () => {
    props.onRemoveHandle?.(handleProps);
    setConfirm(undefined);
  };

  return <div key={props.name}>
    {confim && <ConfirmModal
      open={Boolean(confim)}
      onCancel={() => setConfirm(undefined)}
      {...confim}
    />}

    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: "transparent" }}
      onChange={(ev, expanded) => props.onExpand?.(handleProps, expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography fontWeight="bold" fontSize="0.95rem">{props.name}</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={1}>

          <TextField
            fullWidth
            size="small"
            margin="dense"
            type="select"
            label="Posição"
            select={true}
            value={handleProps.position}
            onChange={(e) => handlePosition(e.target.value)}
          >
            <MenuItem value={Position.Top}>
              Superior
            </MenuItem>

            <MenuItem value={Position.Bottom}>
              Inferior
            </MenuItem>

            <MenuItem value={Position.Left}>
              Esquerda
            </MenuItem>

            <MenuItem value={Position.Right}>
              Direita
            </MenuItem>
          </TextField>

          <TextField
            fullWidth
            size="small"
            margin="dense"
            type="select"
            label="Função"
            select={true}
            value={handleProps.type}
            onChange={(e) => setConfirm({
              title: "Deseja alterar o tipo deste handle?",
              message: "Esta ação implica na exclusão de todas as linhas que chegam no Nó!",
              onConfirm: () => handleType(e.target.value as HandleType),
            })}
          >
            <MenuItem value={'source'}>
              Saída
            </MenuItem>

            <MenuItem value={'target'}>
              Entrada
            </MenuItem>
          </TextField>

          <Button variant='outlined' endIcon={<Delete />} onClick={() => setConfirm({
            title: "Deseja remover este Handle?",
            message: "Esta ação é irreversível!",
            onConfirm: handleRemove,
          })}>
            Remover Handle
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  </div>;
};

interface ItemProps {
  title: string;
  icon?: React.ReactNode;
}
const Item: React.FC<ItemProps> = ({ title, icon }) => {
  return <div
    style={{
      flex: 1,
      gap: "1em",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    }}>
    <Typography>
      {title}
    </Typography>
    {icon}
  </div>;
};

function toRFPosition(pos: 'left' | 'right' | 'top' | 'bottom'): Position {
  switch (pos) {
    case 'left': return Position.Left;
    case 'right': return Position.Right;
    case 'top': return Position.Top;
    case 'bottom': return Position.Bottom;
  }
}
function convertFlow(
  originalFlow: Flow,
  nodes: Node<MyNodeType>[],
  edges: Edge[]
): Flow {
  const convertedNodes: ContextNode[] = nodes.map((node) => {
    const dados = {
      id: node.id,
      flowId: originalFlow.id,
      markdown: node.data.markdown,
      style: { ...node.data.style, parentId: node.parentId },
      posX: node.position.x,
      posY: node.position.y,
      handles: node.data.handles.filter(h => h.id).map((h) => ({
        id: h.id ?? "",
        nodeId: node.id,
        type: h.type,
        position: h.position,
        style: h.style,
      })),
    };
    return dados;
  });

  const convertedEdges: ContextEdge[] = edges.filter(e => e.sourceHandle && e.targetHandle).map((edge) => ({
    id: edge.id,
    flowId: originalFlow.id,
    handleSourceId: edge.sourceHandle ?? "",
    handleTargetId: edge.targetHandle ?? "",
    markdown: edge.data?.markdown as string | undefined,
    style: edge.style,
  }));

  return {
    ...originalFlow,
    nodes: convertedNodes,
    edges: convertedEdges,
  };
}
function isNodeInside(childNode: Node, parentNode: Node) {
  let isInside = false;
  if (childNode.parentId === parentNode.id) isInside = true;

  const childRect = {
    x: childNode.position.x + (isInside ? parentNode.position.x : 0),
    y: childNode.position.y + (isInside ? parentNode.position.y : 0),
    width: childNode.width ?? childNode.measured?.width ?? 0,
    height: childNode.height ?? childNode.measured?.height ?? 0,
  };

  const parentRect = {
    x: parentNode.position.x,
    y: parentNode.position.y,
    width: parentNode.width ?? parentNode.measured?.width ?? 0,
    height: parentNode.height ?? parentNode.measured?.height ?? 0,
  };

  return childRect.x >= parentRect.x &&
    childRect.x + childRect.width <= parentRect.x + parentRect.width &&
    childRect.y >= parentRect.y &&
    childRect.y + childRect.height <= parentRect.y + parentRect.height;
}
function generatePreGroups(
  node: Node<MyNodeType>,
  onAddHandle: () => void,
  onRemoveHandle: (handle: HandleProps) => void,
  onEditHandle: (handle: HandleProps) => void,
): Group[] {

  const array: Group[] = [];

  array.push({
    group: "Conexões",
    fields: [...node.data.handles, 'add'].map((handle, idx) => {
      if (handle === 'add') {
        return {
          name: `Add Handle`,
          label: `Add Handle`,
          type: "component",
          children: <Button key={idx} fullWidth onClick={onAddHandle} variant='contained'>
            Adicionar Conexão
          </Button>,
        };
      }
      return {
        name: `Conexão ${idx + 1}`,
        label: `Conexão ${idx + 1}`,
        type: "component",
        children: <HandleItem
          key={`Conexão ${idx + 1}`} name={`Conexão ${idx + 1}`}
          onRemoveHandle={onRemoveHandle}
          onEditHandle={onEditHandle}
          {...handle as HandleProps}
        />,
      };
    }),
  });

  return array;
}